import { useEffect, useState } from "react";
import {
  fetchJson,
  type KillSwitchStatus,
  type RiskSnapshot,
} from "../lib/api";

interface ProposalResult {
  accepted: boolean;
  correlationId: string;
  mode: string;
  killSwitchActive: boolean;
  message: string;
}

const VENUES = [
  "coinbase",
  "alpaca-live",
  "kalshi-predictions",
  "kalshi-perps",
] as const;

export function TradingPage() {
  const [kill, setKill] = useState<KillSwitchStatus | null>(null);
  const [risk, setRisk] = useState<RiskSnapshot | null>(null);
  const [venue, setVenue] = useState<(typeof VENUES)[number]>("coinbase");
  const [instrument, setInstrument] = useState("BTC-USD");
  const [side, setSide] = useState<"buy" | "sell">("buy");
  const [notionalUsd, setNotionalUsd] = useState("10");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ProposalResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    const [ks, rs] = await Promise.all([
      fetchJson<KillSwitchStatus>("/trading/kill-switch"),
      fetchJson<RiskSnapshot>("/trading/risk"),
    ]);
    setKill(ks);
    setRisk(rs);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const propose = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const body = await fetchJson<ProposalResult>("/trading/propose", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue,
          instrument,
          side,
          notionalUsd: Number(notionalUsd),
          mode: "live",
        }),
      });
      setResult(body);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-pad">
      <div className="page-header">
        <div>
          <h1>Trading</h1>
          <p>
            Live operator surface. Order writes are blocked when the Cloudflare kill switch is active
            or unreachable. Risk numbers mirror playbook/risk.yaml.
          </p>
        </div>
        <span className={`badge ${kill?.active ? "danger" : "ok"}`}>
          Kill switch {kill?.active ? "ON" : "OFF"}
        </span>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="grid three">
        <div className="panel stat">
          <span className="label">Crypto default entry</span>
          <span className="value">${risk?.cryptoDefaultEntryUsd ?? "—"}</span>
        </div>
        <div className="panel stat">
          <span className="label">Kalshi max risk / order</span>
          <span className="value">${risk?.kalshiMaxRiskPerOrderUsd ?? "—"}</span>
        </div>
        <div className="panel stat">
          <span className="label">Alpaca single-position cap</span>
          <span className="value">{risk?.alpacaSinglePositionRiskCapPct ?? "—"}%</span>
        </div>
      </div>

      <div className="panel stack" style={{ marginTop: "1rem" }}>
        <h2>Live proposal</h2>
        <div className="row">
          <label className="field">
            Venue
            <select value={venue} onChange={(e) => setVenue(e.target.value as typeof venue)}>
              {VENUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Instrument
            <input value={instrument} onChange={(e) => setInstrument(e.target.value)} />
          </label>
          <label className="field">
            Side
            <select value={side} onChange={(e) => setSide(e.target.value as "buy" | "sell")}>
              <option value="buy">buy</option>
              <option value="sell">sell</option>
            </select>
          </label>
          <label className="field">
            Notional USD
            <input value={notionalUsd} onChange={(e) => setNotionalUsd(e.target.value)} />
          </label>
        </div>
        <div className="row">
          <button className="btn primary" type="button" disabled={busy || kill?.active} onClick={propose}>
            {busy ? "Submitting…" : "Propose live order"}
          </button>
          <button className="btn" type="button" onClick={() => void refresh()}>
            Refresh status
          </button>
        </div>
        {kill?.active ? (
          <p className="error">Kill switch is active — live proposals are blocked fail-closed.</p>
        ) : null}
        {result ? (
          <div className="feed-item">
            <div className="meta">{result.correlationId}</div>
            <div>
              {result.accepted ? "Accepted" : "Blocked"} · mode={result.mode} ·{" "}
              {result.message}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
